import { useEffect, useState, useCallback } from "react";
import { camoApi } from "@/api";
import {
  ClipboardList, AlertTriangle, Clock, CheckCircle, ShieldCheck,
  Wrench, Plus, X, ChevronDown, RefreshCw, Pencil, Trash2,
  Plane, FileText, List, LayoutDashboard, Info,
} from "lucide-react";
import { toast } from "sonner";

// ─── Config ───────────────────────────────────────────────────────────────────
const PRIORITY_CFG = {
  urgent: { label:"URGENT", cls:"text-red-400 bg-red-400/10 border-red-400/30" },
  high:   { label:"HIGH",   cls:"text-orange-400 bg-orange-400/10 border-orange-400/30" },
  medium: { label:"MEDIUM", cls:"text-amber-400 bg-amber-400/10 border-amber-400/30" },
  low:    { label:"LOW",    cls:"text-slate-400 bg-slate-400/10 border-slate-400/30" },
};
const STATUS_CFG = {
  overdue:     { label:"Overdue",     cls:"text-red-400" },
  in_progress: { label:"In Progress", cls:"text-amber-400" },
  open:        { label:"Open",        cls:"text-blue-400" },
  completed:   { label:"Completed",   cls:"text-green-400" },
  deferred:    { label:"Deferred",    cls:"text-slate-400" },
};
const ARC_CFG = {
  valid:             { label:"Valid",            cls:"text-green-400 bg-green-400/10 border-green-400/30" },
  expiring_soon:     { label:"Expiring Soon",    cls:"text-amber-400 bg-amber-400/10 border-amber-400/30" },
  expiring_critical: { label:"Expiring <30d",    cls:"text-orange-400 bg-orange-400/10 border-orange-400/30" },
  expired:           { label:"EXPIRED",          cls:"text-red-400 bg-red-400/10 border-red-400/30" },
};
const MEL_CAT_CFG = {
  A: { cls:"text-red-400 bg-red-400/10 border-red-400/30",    label:"Cat A", desc:"Next flight" },
  B: { cls:"text-orange-400 bg-orange-400/10 border-orange-400/30", label:"Cat B", desc:"3 days" },
  C: { cls:"text-amber-400 bg-amber-400/10 border-amber-400/30",    label:"Cat C", desc:"10 days" },
  D: { cls:"text-blue-400 bg-blue-400/10 border-blue-400/30",  label:"Cat D", desc:"120 days" },
};
const MEL_STATUS_CFG = {
  active:   { label:"Active",   cls:"text-amber-400" },
  cleared:  { label:"Cleared",  cls:"text-green-400" },
  expired:  { label:"EXPIRED",  cls:"text-red-400" },
  extended: { label:"Extended", cls:"text-blue-400" },
};
const AMP_STATUS_CFG = {
  current:   { label:"Current",   cls:"text-green-400 bg-green-400/10 border-green-400/30" },
  due_soon:  { label:"Due Soon",  cls:"text-amber-400 bg-amber-400/10 border-amber-400/30" },
  overdue:   { label:"OVERDUE",   cls:"text-red-400 bg-red-400/10 border-red-400/30" },
};

const CATEGORIES = ["Airworthiness Directive","Airworthiness Review","Service Bulletin",
  "Maintenance Planning","MEL Review","Records Management","Reliability","Technical Investigation",
  "Operations","Quality Assurance","Planning"];
const PLANNERS = ["P. Krishnan","M. Shetty","R. Bhatia","S. Nair"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
}
function daysLabel(days) {
  if (days === undefined || days === null) return "—";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  return `${days}d left`;
}
function daysColor(days) {
  if (days < 0) return "text-red-400";
  if (days <= 10) return "text-orange-400";
  if (days <= 30) return "text-amber-400";
  return "text-green-400";
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, color, Icon, sub }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs tracking-widest uppercase text-muted-foreground font-bold">{label}</span>
        <Icon size={14} className={color} />
      </div>
      <div className={`text-3xl font-mono font-medium ${color}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

// ─── Tab Button ───────────────────────────────────────────────────────────────
function Tab({ id, label, Icon, active, onClick, badge }) {
  return (
    <button onClick={() => onClick(id)} data-testid={`camo-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors whitespace-nowrap ${
        active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}>
      <Icon size={12} />
      {label}
      {badge > 0 && (
        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400">{badge}</span>
      )}
    </button>
  );
}

// ─── Slide-Out Drawer ─────────────────────────────────────────────────────────
function Drawer({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border-l border-border flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="text-sm font-bold text-foreground tracking-tight">{title}</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
const inputCls = "w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none";
const selectCls = inputCls + " appearance-none";

// ─── SOURCE BADGE ─────────────────────────────────────────────────────────────
function SourceBadge({ source }) {
  if (source === "internal") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold border text-blue-400 bg-blue-400/10 border-blue-400/30">
      <List size={9} /> Fatigue Shield
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold border text-purple-400 bg-purple-400/10 border-purple-400/30">
      <RefreshCw size={9} /> API Sync
    </span>
  );
}

// ─── Planner workload calculation ─────────────────────────────────────────────
const PRIORITY_WEIGHT = { urgent: 4, high: 3, medium: 2, low: 1 };
function calcPlannerWorkloads(tasks) {
  const activeTasks = tasks.filter(t => t.status !== "completed" && t.status !== "deferred");
  const plannerMap = {};
  for (const t of activeTasks) {
    const p = t.assigned_planner || "Unassigned";
    if (!plannerMap[p]) plannerMap[p] = [];
    plannerMap[p].push(t);
  }
  return Object.entries(plannerMap).map(([name, pts]) => {
    const baseScore = pts.reduce((s, t) => s + (PRIORITY_WEIGHT[t.priority] || 1) * 10, 0);
    const overdueBonus = pts.filter(t => t.status === "overdue").length * 8;
    const score = Math.min(100, Math.round(baseScore + overdueBonus));
    return { name, taskCount: pts.length, score };
  }).sort((a, b) => b.score - a.score);
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ overview, arc, mel, aircraft, tasks }) {
  const fleetStatus = aircraft.map(a => {
    const arcRec = arc.find(r => r.aircraft_tail === a.tail_number);
    const melCount = mel.filter(m => m.aircraft_tail === a.tail_number && m.status === "active").length;
    return { ...a, arcStatus: arcRec?.status || "no_arc", arcExpiry: arcRec?.days_to_expiry, melCount };
  });

  const urgentCount     = tasks.filter(t => t.priority === "urgent" && t.status !== "completed").length;
  const plannerLoads    = calcPlannerWorkloads(tasks);
  const overloadedCount = plannerLoads.filter(p => p.score > 75).length;
  const overloadedNames = plannerLoads.filter(p => p.score > 75).map(p => p.name);

  // Active tasks for queue (non-completed, non-deferred), sorted by urgency then due date
  const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };
  const queueTasks = tasks
    .filter(t => t.status !== "completed" && t.status !== "deferred")
    .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));

  function workloadBar(planner) {
    const rec = plannerLoads.find(p => p.name === planner);
    if (!rec) return null;
    const score = rec.score;
    const barColor = score >= 80 ? "bg-red-500" : score >= 60 ? "bg-amber-500" : "bg-green-500";
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score}%` }} />
        </div>
        <span className={`font-mono text-xs font-bold ${score >= 80 ? "text-red-400" : score >= 60 ? "text-amber-400" : "text-green-400"}`}>{score}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Top KPI row — matches original CAMO Planning Dashboard ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Tasks"        value={overview?.total_tasks}   color="text-primary"   Icon={ClipboardList}/>
        <KpiCard label="Overdue"            value={overview?.overdue_tasks} color="text-red-400"   Icon={AlertTriangle}/>
        <KpiCard label="Urgent"             value={urgentCount}             color="text-orange-400"Icon={Clock}/>
        <KpiCard label="Overloaded Planners"value={overloadedCount}         color="text-amber-400" Icon={AlertTriangle} sub="workload score > 75"/>
      </div>

      {/* ── Planner fatigue banner (shown only when overloaded planners exist) ── */}
      {overloadedCount > 0 && (
        <div className="flex items-start gap-3 p-4 border border-amber-500/30 bg-amber-500/8 rounded-sm" data-testid="camo-overload-banner">
          <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200 leading-relaxed">
            <strong>{overloadedCount} planner{overloadedCount > 1 ? "s" : ""}</strong> {overloadedCount > 1 ? "are" : "is"} showing elevated cognitive fatigue scores
            ({overloadedNames.join(", ")}). Quality control prompts are active for critical document reviews.
            Consider redistributing task load.
          </div>
        </div>
      )}

      {/* ── Task Queue ── */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Task Queue</div>
          <span className="text-xs text-muted-foreground">{queueTasks.length} task{queueTasks.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="camo-overview-task-queue">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {["Task ID","Title","Category","Priority","Status","Assigned To","Workload","Due Date"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {queueTasks.map((t, i) => {
                const pr = PRIORITY_CFG[t.priority] || PRIORITY_CFG.medium;
                const st = STATUS_CFG[t.status]     || STATUS_CFG.open;
                const isOverdue = t.status === "overdue";
                return (
                  <tr key={t.id} className={`border-b border-border/50 hover:bg-secondary/30 ${isOverdue ? "bg-red-500/5" : i % 2 !== 0 ? "bg-secondary/10" : ""}`}>
                    <td className="px-3 py-2.5 font-mono font-bold text-blue-400">{t.task_id}</td>
                    <td className="px-3 py-2.5 font-medium text-foreground max-w-48 truncate">{t.title}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{t.category}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-sm font-bold border text-xs ${pr.cls}`}>{pr.label}</span>
                    </td>
                    <td className={`px-3 py-2.5 font-medium whitespace-nowrap ${st.cls}`}>{st.label}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{t.assigned_planner || "—"}</td>
                    <td className="px-3 py-2.5">{workloadBar(t.assigned_planner)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap font-mono">{fmtDate(t.due_date)}</td>
                  </tr>
                );
              })}
              {queueTasks.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No active tasks</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Secondary: ARC / MEL KPIs ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="ARC Expiring"   value={overview?.arc_expiring}  color="text-amber-400" Icon={ShieldCheck}   sub="within 60 days"/>
        <KpiCard label="ARC Expired"    value={overview?.arc_expired}   color="text-red-400"   Icon={AlertTriangle}/>
        <KpiCard label="MEL Active"     value={overview?.mel_active}    color="text-orange-400"Icon={Wrench}/>
        <KpiCard label="MEL Overrun"    value={overview?.mel_expired}   color="text-red-400"   Icon={AlertTriangle} sub="past expiry"/>
      </div>

      {/* ── Fleet Airworthiness Matrix ── */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Fleet Airworthiness Matrix</div>
          <div className="text-xs text-muted-foreground mt-0.5">{aircraft.length} aircraft — ARC status + active MEL deferrals</div>
        </div>
        <div className="p-4 grid grid-cols-3 lg:grid-cols-6 gap-2">
          {fleetStatus.map(a => {
            const arc_c = ARC_CFG[a.arcStatus] || ARC_CFG.valid;
            const hasMel = a.melCount > 0;
            return (
              <div key={a.tail_number} className={`rounded-sm border p-2.5 ${a.arcStatus === "expired" ? "border-red-500/40 bg-red-500/5" : "border-border bg-background/40"}`}>
                <div className="font-mono font-bold text-foreground text-xs">{a.tail_number}</div>
                <div className="text-[10px] text-muted-foreground mb-2">{a.aircraft_type.replace("Boeing ","B").replace("Airbus ","")}</div>
                <span className={`text-[10px] font-bold border px-1.5 py-0.5 rounded-sm ${arc_c.cls}`}>{arc_c.label}</span>
                {hasMel && <div className="text-[10px] text-amber-400 mt-1">{a.melCount} MEL active</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Data Source Info ── */}
      <div className="flex items-start gap-3 p-4 border border-blue-500/20 bg-blue-500/5 rounded-sm">
        <Info size={14} className="text-blue-400 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-300 leading-relaxed">
          <strong>Data Source:</strong> All tasks, ARC records, and MEL deferrals shown here are managed natively within Fatigue Shield (Option A — self-contained).
          In a live integration (Option B), tasks can also be pulled automatically via API from your existing MRO system —
          <strong> AMOS, OASES, Ramco Aviation,</strong> or <strong>TRAX</strong> — with the source displayed on each record.
        </div>
      </div>
    </div>
  );
}

// ─── Tasks Tab ────────────────────────────────────────────────────────────────
function TasksTab({ tasks, aircraft, onRefresh, onArcNeeded }) {
  const [filter, setFilter] = useState({ status:"", priority:"", aircraft_tail:"" });
  const [showCreate, setShowCreate] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({});

  const filtered = tasks.filter(t =>
    (!filter.status        || t.status === filter.status) &&
    (!filter.priority      || t.priority === filter.priority) &&
    (!filter.aircraft_tail || t.aircraft_tail === filter.aircraft_tail)
  );

  function openCreate() { setForm({ priority:"medium", category:"Maintenance Planning", assigned_planner: PLANNERS[0] }); setShowCreate(true); }
  function openEdit(t)  { setForm({ ...t }); setEditTask(t); }
  function closeDrawer() { setShowCreate(false); setEditTask(null); setForm({}); }

  async function handleSave() {
    if (!form.title || !form.category || !form.due_date || !form.assigned_planner) {
      toast.error("Please fill all required fields"); return;
    }
    try {
      if (editTask) {
        await camoApi.updateTask(editTask.id, form);
        toast.success("Task updated");
      } else {
        await camoApi.createTask(form);
        toast.success("Task created");
      }
      closeDrawer(); onRefresh();
    } catch { toast.error("Failed to save task"); }
  }

  async function handleDelete(task) {
    if (!window.confirm(`Delete task ${task.task_id}?`)) return;
    try { await camoApi.deleteTask(task.id); toast.success("Task deleted"); onRefresh(); }
    catch { toast.error("Failed to delete task"); }
  }

  async function quickStatus(task, status) {
    try {
      await camoApi.updateTask(task.id, { status });
      onRefresh();
      // When an Airworthiness Review task is completed for a specific aircraft,
      // prompt the planner to update the actual ARC record with new certificate details.
      if (
        status === "completed" &&
        task.category === "Airworthiness Review" &&
        task.aircraft_tail
      ) {
        toast("ARC Renewal task completed", {
          description: `Task ${task.task_id} done — but ${task.aircraft_tail} ARC certificate is still EXPIRED until you enter new certificate details.`,
          duration: 10000,
          action: {
            label: `Update ARC for ${task.aircraft_tail} →`,
            onClick: () => onArcNeeded(task.aircraft_tail),
          },
        });
      } else {
        toast.success(`Status → ${status}`);
      }
    } catch { toast.error("Update failed"); }
  }

  return (
    <div className="space-y-4">
      {/* Filters + Create */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-2 flex-wrap">
          {[["status",["","open","in_progress","overdue","completed","deferred"]],
            ["priority",["","urgent","high","medium","low"]]].map(([k, opts]) => (
            <select key={k} value={filter[k]} onChange={e => setFilter(p=>({...p,[k]:e.target.value}))}
              className={selectCls + " w-36 text-xs py-1.5"}>
              {opts.map(o => <option key={o} value={o}>{o || `All ${k}`}</option>)}
            </select>
          ))}
          <select value={filter.aircraft_tail} onChange={e => setFilter(p=>({...p,aircraft_tail:e.target.value}))}
            className={selectCls + " w-36 text-xs py-1.5"}>
            <option value="">All Aircraft</option>
            {aircraft.map(a => <option key={a.tail_number} value={a.tail_number}>{a.tail_number}</option>)}
          </select>
        </div>
        <button onClick={openCreate} data-testid="create-task-btn"
          className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-sm text-xs font-bold hover:bg-primary/80">
          <Plus size={12} /> New Task
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border text-xs text-muted-foreground">{filtered.length} tasks</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="camo-tasks-table">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {["ID","Title","Category","Aircraft","Priority","Status","Assigned","Due","Source","Actions"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const pc = PRIORITY_CFG[t.priority] || PRIORITY_CFG.low;
                const sc = STATUS_CFG[t.status] || {};
                return (
                  <tr key={t.id} className={`border-b border-border/50 hover:bg-secondary/30 ${t.status === "overdue" ? "bg-red-500/5" : ""}`} data-testid={`camo-task-${t.task_id}`}>
                    <td className="px-3 py-2.5 font-mono text-primary">{t.task_id}</td>
                    <td className="px-3 py-2.5 max-w-[180px]"><div className="truncate text-foreground">{t.title}</div></td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{t.category}</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground">{t.aircraft_tail || "—"}</td>
                    <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-sm font-bold border ${pc.cls}`}>{pc.label}</span></td>
                    <td className="px-3 py-2.5">
                      <select value={t.status} onChange={e => quickStatus(t, e.target.value)}
                        className={`${sc.cls} bg-transparent border-0 font-bold text-xs cursor-pointer focus:outline-none`}>
                        {Object.entries(STATUS_CFG).map(([v,c]) => <option key={v} value={v} className="text-foreground bg-card">{c.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{t.assigned_planner}</td>
                    <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${t.status==="overdue"?"text-red-400":"text-muted-foreground"}`}>{fmtDate(t.due_date)}</td>
                    <td className="px-3 py-2.5"><SourceBadge source={t.source}/></td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(t)} data-testid={`edit-task-${t.task_id}`} className="text-muted-foreground hover:text-primary"><Pencil size={12}/></button>
                        <button onClick={() => handleDelete(t)} data-testid={`delete-task-${t.task_id}`} className="text-muted-foreground hover:text-red-400"><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={showCreate || !!editTask} onClose={closeDrawer}
        title={editTask ? `Edit Task — ${editTask.task_id}` : "Create New CAMO Task"}>
        <FormField label="Title" required>
          <input className={inputCls} placeholder="Task title..." value={form.title||""} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/>
        </FormField>
        <FormField label="Category" required>
          <select className={selectCls} value={form.category||""} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
            <option value="">Select category</option>
            {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Priority" required>
            <select className={selectCls} value={form.priority||"medium"} onChange={e=>setForm(p=>({...p,priority:e.target.value}))}>
              {["urgent","high","medium","low"].map(v=><option key={v} value={v}>{v}</option>)}
            </select>
          </FormField>
          <FormField label="Aircraft Tail">
            <select className={selectCls} value={form.aircraft_tail||""} onChange={e=>setForm(p=>({...p,aircraft_tail:e.target.value}))}>
              <option value="">None</option>
              {aircraft.map(a=><option key={a.tail_number} value={a.tail_number}>{a.tail_number}</option>)}
            </select>
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="ATA Reference">
            <input className={inputCls} placeholder="e.g. ATA-28" value={form.ata_ref||""} onChange={e=>setForm(p=>({...p,ata_ref:e.target.value}))}/>
          </FormField>
          <FormField label="Due Date" required>
            <input type="date" className={inputCls} value={form.due_date?.slice(0,10)||""} onChange={e=>setForm(p=>({...p,due_date:e.target.value}))}/>
          </FormField>
        </div>
        <FormField label="Assigned Planner" required>
          <select className={selectCls} value={form.assigned_planner||PLANNERS[0]} onChange={e=>setForm(p=>({...p,assigned_planner:e.target.value}))}>
            {PLANNERS.map(pl=><option key={pl} value={pl}>{pl}</option>)}
          </select>
        </FormField>
        <FormField label="Notes">
          <textarea className={inputCls + " h-20 resize-none"} placeholder="Optional notes..." value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
        </FormField>
        <button onClick={handleSave} data-testid="save-task-btn"
          className="w-full py-2.5 bg-primary text-white font-bold text-sm rounded-sm hover:bg-primary/80">
          {editTask ? "Update Task" : "Create Task"}
        </button>
      </Drawer>
    </div>
  );
}

// ─── ARC Tab ──────────────────────────────────────────────────────────────────
function ARCTab({ arc, aircraft, onRefresh, pendingArcTail, onArcClear }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [form, setForm] = useState({});

  const sorted = [...arc].sort((a,b) => (a.days_to_expiry||999) - (b.days_to_expiry||999));

  // When parent signals a pending ARC update (from a completed renewal task),
  // auto-open the edit drawer for that specific aircraft.
  useEffect(() => {
    if (pendingArcTail) {
      const rec = arc.find(r => r.aircraft_tail === pendingArcTail);
      if (rec) {
        setForm({ ...rec, issue_date: rec.issue_date?.slice(0,10), expiry_date: rec.expiry_date?.slice(0,10) });
        setEditRec(rec);
      }
    }
  }, [pendingArcTail, arc]);

  function openCreate() { setForm({}); setShowCreate(true); }
  function openEdit(r) { setForm({ ...r, issue_date: r.issue_date?.slice(0,10), expiry_date: r.expiry_date?.slice(0,10) }); setEditRec(r); }
  function closeDrawer() { setShowCreate(false); setEditRec(null); setForm({}); if (onArcClear) onArcClear(); }

  async function handleSave() {
    if (!form.aircraft_tail || !form.arc_number || !form.issue_date || !form.expiry_date || !form.reviewer) {
      toast.error("Please fill all required fields"); return;
    }
    try {
      if (editRec) {
        await camoApi.updateArc(editRec.id, { ...form, expiry_date: new Date(form.expiry_date).toISOString() });
        toast.success("ARC updated");
      } else {
        await camoApi.createArc({ ...form, issue_date: new Date(form.issue_date).toISOString(), expiry_date: new Date(form.expiry_date).toISOString() });
        toast.success("ARC record created");
      }
      closeDrawer(); onRefresh();
    } catch { toast.error("Failed to save ARC"); }
  }

  return (
    <div className="space-y-4">
      {/* Banner shown when navigated here from a completed ARC renewal task */}
      {pendingArcTail && (
        <div className="flex items-start gap-3 p-4 border border-amber-500/40 bg-amber-500/10 rounded-sm" data-testid="arc-pending-banner">
          <CheckCircle size={15} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-300 mb-0.5">
              ARC Renewal Task Completed for {pendingArcTail}
            </div>
            <p className="text-xs text-amber-200/80">
              The planning task is done — but the certificate remains <strong>EXPIRED</strong> until
              you record the new ARC number and expiry date below.
              The edit drawer has been opened for you.
            </p>
          </div>
          <button onClick={onArcClear} className="text-amber-400/60 hover:text-amber-400"><X size={14}/></button>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="text-xs text-muted-foreground">{arc.length} aircraft · {arc.filter(a=>a.status==="expired").length} expired · {arc.filter(a=>a.status.startsWith("expiring")).length} expiring</div>
        <button onClick={openCreate} data-testid="create-arc-btn"
          className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-sm text-xs font-bold hover:bg-primary/80">
          <Plus size={12} /> Renew / Add ARC
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-xs" data-testid="camo-arc-table">
          <thead>
            <tr className="border-b border-border bg-background/40">
              {["Aircraft","Type","ARC Number","Issue Date","Expiry Date","Days to Expiry","Status","Reviewer","Source","Action"].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const ac = aircraft.find(a => a.tail_number === r.aircraft_tail);
              const sc = ARC_CFG[r.status] || ARC_CFG.valid;
              return (
                <tr key={r.id} className={`border-b border-border/50 hover:bg-secondary/30 ${r.status==="expired"?"bg-red-500/5":""}`} data-testid={`arc-row-${r.aircraft_tail}`}>
                  <td className="px-3 py-2.5 font-mono font-bold text-foreground">{r.aircraft_tail}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{ac?.aircraft_type?.replace("Boeing ","B").replace("Airbus ","") || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-primary">{r.arc_number}</td>
                  <td className="px-3 py-2.5 font-mono text-muted-foreground whitespace-nowrap">{fmtDate(r.issue_date)}</td>
                  <td className={`px-3 py-2.5 font-mono font-bold whitespace-nowrap ${r.status==="expired"?"text-red-400":"text-foreground"}`}>{fmtDate(r.expiry_date)}</td>
                  <td className={`px-3 py-2.5 font-mono font-bold ${daysColor(r.days_to_expiry)}`}>{daysLabel(r.days_to_expiry)}</td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-sm font-bold border text-xs ${sc.cls}`}>{sc.label}</span></td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{r.reviewer}</td>
                  <td className="px-3 py-2.5"><SourceBadge source={r.source}/></td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary"><Pencil size={12}/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Drawer open={showCreate || !!editRec} onClose={closeDrawer}
        title={editRec ? `Update ARC — ${editRec.aircraft_tail}` : "New ARC Record"}>
        <FormField label="Aircraft Tail" required>
          <select className={selectCls} value={form.aircraft_tail||""} onChange={e=>setForm(p=>({...p,aircraft_tail:e.target.value}))}>
            <option value="">Select aircraft</option>
            {aircraft.map(a=><option key={a.tail_number} value={a.tail_number}>{a.tail_number} — {a.aircraft_type}</option>)}
          </select>
        </FormField>
        <FormField label="ARC Number" required>
          <input className={inputCls} placeholder="e.g. ARC-2025-001" value={form.arc_number||""} onChange={e=>setForm(p=>({...p,arc_number:e.target.value}))}/>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Issue Date" required>
            <input type="date" className={inputCls} value={form.issue_date||""} onChange={e=>setForm(p=>({...p,issue_date:e.target.value}))}/>
          </FormField>
          <FormField label="Expiry Date" required>
            <input type="date" className={inputCls} value={form.expiry_date||""} onChange={e=>setForm(p=>({...p,expiry_date:e.target.value}))}/>
          </FormField>
        </div>
        <FormField label="Reviewer" required>
          <input className={inputCls} placeholder="Name and designation" value={form.reviewer||""} onChange={e=>setForm(p=>({...p,reviewer:e.target.value}))}/>
        </FormField>
        <FormField label="Notes">
          <textarea className={inputCls + " h-20 resize-none"} value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
        </FormField>
        <button onClick={handleSave} data-testid="save-arc-btn"
          className="w-full py-2.5 bg-primary text-white font-bold text-sm rounded-sm hover:bg-primary/80">
          {editRec ? "Update ARC" : "Create ARC Record"}
        </button>
      </Drawer>
    </div>
  );
}

// ─── MEL Tab ──────────────────────────────────────────────────────────────────
function MELTab({ mel, aircraft, onRefresh }) {
  const [filterAc, setFilterAc] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({});

  const filtered = mel.filter(m =>
    (!filterAc     || m.aircraft_tail === filterAc) &&
    (!filterCat    || m.category === filterCat) &&
    (!filterStatus || m.status === filterStatus)
  );

  async function clearDeferral(m) {
    try {
      await camoApi.updateMel(m.id, { status:"cleared", cleared_by: "Ops Planner" });
      toast.success(`MEL ${m.mel_ref} cleared`);
      onRefresh();
    } catch { toast.error("Failed to clear MEL"); }
  }

  async function handleCreate() {
    if (!form.aircraft_tail||!form.item_ref||!form.ata_chapter||!form.description||!form.category||!form.deferred_date||!form.deferred_by) {
      toast.error("Please fill all required fields"); return;
    }
    try {
      await camoApi.createMel({ ...form, deferred_date: new Date(form.deferred_date).toISOString() });
      toast.success("MEL deferral created");
      setShowCreate(false); setForm({}); onRefresh();
    } catch { toast.error("Failed to create MEL deferral"); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-2 flex-wrap">
          <select value={filterAc} onChange={e=>setFilterAc(e.target.value)} className={selectCls+" w-32 text-xs py-1.5"}>
            <option value="">All Aircraft</option>
            {aircraft.map(a=><option key={a.tail_number} value={a.tail_number}>{a.tail_number}</option>)}
          </select>
          <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} className={selectCls+" w-28 text-xs py-1.5"}>
            <option value="">All Cats</option>
            {["A","B","C","D"].map(c=><option key={c} value={c}>Cat {c}</option>)}
          </select>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} className={selectCls+" w-28 text-xs py-1.5"}>
            <option value="">All Status</option>
            {Object.entries(MEL_STATUS_CFG).map(([v,c])=><option key={v} value={v}>{c.label}</option>)}
          </select>
        </div>
        <button onClick={()=>{setForm({category:"D"});setShowCreate(true);}} data-testid="create-mel-btn"
          className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-sm text-xs font-bold hover:bg-primary/80">
          <Plus size={12}/> New Deferral
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border text-xs text-muted-foreground">{filtered.length} deferrals</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="camo-mel-table">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {["Ref","Aircraft","ATA","Description","Cat","Deferred","Expiry","Days Rem.","Status","Source","Action"].map(h=>(
                  <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => {
                const cc = MEL_CAT_CFG[m.category] || MEL_CAT_CFG.D;
                const sc = MEL_STATUS_CFG[m.status] || {};
                return (
                  <tr key={m.id} className={`border-b border-border/50 hover:bg-secondary/30 ${m.status==="expired"?"bg-red-500/5":""}`} data-testid={`mel-row-${m.mel_ref}`}>
                    <td className="px-3 py-2.5 font-mono text-primary">{m.mel_ref}</td>
                    <td className="px-3 py-2.5 font-mono font-bold text-foreground">{m.aircraft_tail}</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground">{m.ata_chapter}</td>
                    <td className="px-3 py-2.5 text-foreground max-w-[200px]"><div className="truncate">{m.description}</div></td>
                    <td className="px-3 py-2.5">
                      <div title={cc.desc} className={`px-1.5 py-0.5 rounded-sm font-bold border text-xs cursor-help ${cc.cls}`}>{cc.label}</div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground whitespace-nowrap">{fmtDate(m.deferred_date)}</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground whitespace-nowrap">{fmtDate(m.expiry_date)}</td>
                    <td className={`px-3 py-2.5 font-mono font-bold ${daysColor(m.days_remaining)}`}>{daysLabel(m.days_remaining)}</td>
                    <td className={`px-3 py-2.5 font-bold ${sc.cls}`}>{sc.label}</td>
                    <td className="px-3 py-2.5"><SourceBadge source={m.source}/></td>
                    <td className="px-3 py-2.5">
                      {m.status === "active" && (
                        <button onClick={()=>clearDeferral(m)} data-testid={`clear-mel-${m.mel_ref}`}
                          className="text-xs text-green-400 hover:underline font-bold">Clear</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={showCreate} onClose={()=>{setShowCreate(false);setForm({});}} title="New MEL Deferral">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Aircraft Tail" required>
            <select className={selectCls} value={form.aircraft_tail||""} onChange={e=>setForm(p=>({...p,aircraft_tail:e.target.value}))}>
              <option value="">Select</option>
              {aircraft.map(a=><option key={a.tail_number} value={a.tail_number}>{a.tail_number}</option>)}
            </select>
          </FormField>
          <FormField label="Category" required>
            <select className={selectCls} value={form.category||"D"} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
              {["A","B","C","D"].map(c=><option key={c} value={c}>Cat {c} ({["Next flight","3 days","10 days","120 days"][["A","B","C","D"].indexOf(c)]})</option>)}
            </select>
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="MEL Item Ref" required>
            <input className={inputCls} placeholder="e.g. B737-28-001" value={form.item_ref||""} onChange={e=>setForm(p=>({...p,item_ref:e.target.value}))}/>
          </FormField>
          <FormField label="ATA Chapter" required>
            <input className={inputCls} placeholder="e.g. 28" value={form.ata_chapter||""} onChange={e=>setForm(p=>({...p,ata_chapter:e.target.value}))}/>
          </FormField>
        </div>
        <FormField label="Description" required>
          <textarea className={inputCls+" h-20 resize-none"} placeholder="Describe the inoperative item..." value={form.description||""} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Deferred Date" required>
            <input type="date" className={inputCls} value={form.deferred_date||""} onChange={e=>setForm(p=>({...p,deferred_date:e.target.value}))}/>
          </FormField>
          <FormField label="Deferred By" required>
            <input className={inputCls} placeholder="Name / Licence No." value={form.deferred_by||""} onChange={e=>setForm(p=>({...p,deferred_by:e.target.value}))}/>
          </FormField>
        </div>
        <FormField label="Notes">
          <textarea className={inputCls+" h-16 resize-none"} value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
        </FormField>
        <button onClick={handleCreate} data-testid="save-mel-btn"
          className="w-full py-2.5 bg-primary text-white font-bold text-sm rounded-sm hover:bg-primary/80">
          Create MEL Deferral
        </button>
      </Drawer>
    </div>
  );
}

// ─── AMP Tab ──────────────────────────────────────────────────────────────────
function AMPTab({ amp, aircraft }) {
  const grouped = aircraft.reduce((acc, a) => {
    acc[a.tail_number] = amp.filter(r => r.aircraft_tail === a.tail_number);
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 p-3 border border-amber-500/20 bg-amber-500/5 rounded-sm">
        <Info size={13} className="text-amber-400 shrink-0 mt-0.5"/>
        <p className="text-xs text-amber-300">
          AMP compliance data is derived from aircraft flight-hour records. In a live environment this is
          automatically updated via integration with your MRO system (AMOS / OASES / Ramco). Overdue checks
          trigger a CAMO task automatically.
        </p>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-xs" data-testid="camo-amp-table">
          <thead>
            <tr className="border-b border-border bg-background/40">
              {["Aircraft","Check Type","Interval (FH)","Last @ FH","Current FH","Next Due @ FH","FH Remaining","Status"].map(h=>(
                <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {amp.map(r => {
              const sc = AMP_STATUS_CFG[r.status] || AMP_STATUS_CFG.current;
              const pct = Math.min(100, Math.max(0, ((r.current_total_fh - r.last_performed_fh) / r.interval_fh) * 100));
              return (
                <tr key={r.id} className={`border-b border-border/50 hover:bg-secondary/30 ${r.status==="overdue"?"bg-red-500/5":""}`} data-testid={`amp-row-${r.aircraft_tail}-${r.check_type.replace(/\s/g,"-")}`}>
                  <td className="px-3 py-2.5 font-mono font-bold text-foreground">{r.aircraft_tail}</td>
                  <td className="px-3 py-2.5 text-foreground font-medium">{r.check_type}</td>
                  <td className="px-3 py-2.5 font-mono text-muted-foreground">{r.interval_fh.toLocaleString()}</td>
                  <td className="px-3 py-2.5 font-mono text-muted-foreground">{r.last_performed_fh.toLocaleString()}</td>
                  <td className="px-3 py-2.5 font-mono text-foreground">{r.current_total_fh.toLocaleString()}</td>
                  <td className="px-3 py-2.5 font-mono text-muted-foreground">{r.next_due_fh.toLocaleString()}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden w-20">
                        <div className={`h-full rounded-full ${pct >= 90?"bg-red-500":pct>=70?"bg-amber-500":"bg-green-500"}`} style={{width:`${pct}%`}}/>
                      </div>
                      <span className={`font-mono font-bold ${r.fh_remaining < 0 ? "text-red-400" : r.fh_remaining < 200 ? "text-amber-400" : "text-muted-foreground"}`}>
                        {r.fh_remaining < 0 ? `+${Math.abs(r.fh_remaining)} OD` : `${r.fh_remaining} FH`}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-sm font-bold border text-xs ${sc.cls}`}>{sc.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CAMOPlanning() {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [tasks,    setTasks]    = useState([]);
  const [arc,      setArc]      = useState([]);
  const [mel,      setMel]      = useState([]);
  const [amp,      setAmp]      = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [overview, setOverview] = useState(null);
  // Pending ARC update: set when an ARC Renewal task is completed
  const [pendingArcTail, setPendingArcTail] = useState(null);

  function handleArcNeeded(tail) {
    setPendingArcTail(tail);
    setTab("arc");
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, m, am, ac, ov] = await Promise.all([
        camoApi.getTasks(), camoApi.getArc(), camoApi.getMel(),
        camoApi.getAmp(), camoApi.getAircraft(), camoApi.getOverview(),
      ]);
      setTasks(t.data); setArc(a.data); setMel(m.data);
      setAmp(am.data); setAircraft(ac.data); setOverview(ov.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex-1 p-6 flex items-center justify-center">
      <div className="text-primary font-mono text-sm animate-pulse">LOADING CAMO DATA...</div>
    </div>
  );

  const tabs = [
    { id:"overview", label:"Overview",      Icon:LayoutDashboard },
    { id:"tasks",    label:"Tasks",         Icon:ClipboardList,  badge: overview?.overdue_tasks },
    { id:"arc",      label:"ARC Tracking",  Icon:ShieldCheck,    badge: (overview?.arc_expiring||0)+(overview?.arc_expired||0) },
    { id:"mel",      label:"MEL Deferrals", Icon:Wrench,         badge: overview?.mel_expired },
    { id:"amp",      label:"AMP Compliance",Icon:FileText },
  ];

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="camo-planning">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">CAMO Module</div>
          <h1 className="text-2xl font-black font-heading text-foreground">Continuing Airworthiness Management</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {aircraft.length} aircraft · {tasks.length} tasks · {arc.length} ARC records · {mel.filter(m=>m.status==="active").length} active MEL deferrals
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-sm text-xs text-muted-foreground hover:text-foreground">
          <RefreshCw size={11}/> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto" data-testid="camo-tabs">
        {tabs.map(t => <Tab key={t.id} {...t} active={tab===t.id} onClick={setTab}/>)}
      </div>

      {tab === "overview" && <OverviewTab overview={overview} arc={arc} mel={mel} aircraft={aircraft} tasks={tasks}/>}
      {tab === "tasks"    && <TasksTab tasks={tasks} aircraft={aircraft} onRefresh={load} onArcNeeded={handleArcNeeded}/>}
      {tab === "arc"      && <ARCTab arc={arc} aircraft={aircraft} onRefresh={load} pendingArcTail={pendingArcTail} onArcClear={()=>setPendingArcTail(null)}/>}
      {tab === "mel"      && <MELTab mel={mel} aircraft={aircraft} onRefresh={load}/>}
      {tab === "amp"      && <AMPTab amp={amp} aircraft={aircraft}/>}
    </div>
  );
}
